<template>
  <b-modal
    :active="value"
    has-modal-card
    trap-focus
    @close="close"
  >
    <div class="modal-card w-full max-w-[960px] mx-auto">
      <header class="modal-card-head">
        <p class="modal-card-title">{{ $t('warehouse_traceability_title') }}</p>
        <button type="button" class="delete" @click="close" />
      </header>
      <section class="modal-card-body relative">
        <b-loading :active="isLoading" :is-full-page="false" />

        <div v-if="header.unique_material_id || header.packing_list_number" class="mb-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700">
          <div v-if="header.unique_material_id">
            <span class="font-semibold text-gray-900">{{ $t('unique_material_id') }}:</span>
            <span class="ml-1">{{ header.unique_material_id }}</span>
          </div>
          <div v-if="header.packing_list_number">
            <span class="font-semibold text-gray-900">{{ $t('packing_list') }}:</span>
            <span class="ml-1">{{ header.packing_list_number }}</span>
          </div>
          <div v-if="header.description" class="basis-full text-gray-600">
            {{ header.description }}
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5">
          <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div class="text-[0.7rem] uppercase tracking-wide text-gray-500">{{ $t('inputs') }}</div>
            <div class="text-xl font-bold text-blue-900 leading-tight">{{ formatQty(data.received_total) }}</div>
          </div>
          <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div class="text-[0.7rem] uppercase tracking-wide text-gray-500">{{ $t('outputs') }}</div>
            <div class="text-xl font-bold text-blue-900 leading-tight">{{ formatQty(data.delivered_total) }}</div>
          </div>
          <div class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
            <div class="text-[0.7rem] uppercase tracking-wide text-gray-500">{{ $t('returns') }}</div>
            <div class="text-xl font-bold text-blue-900 leading-tight">{{ formatQty(data.returned_total) }}</div>
          </div>
          <div class="rounded-md border border-blue-300 bg-blue-50 px-3 py-2">
            <div class="text-[0.7rem] uppercase tracking-wide text-blue-700">{{ $t('stock') }}</div>
            <div class="text-xl font-bold text-blue-900 leading-tight">{{ formatQty(data.current_stock) }}</div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h5 class="text-xs font-semibold text-blue-900 mb-1">{{ $t('outputs') }}</h5>
            <div class="rounded-md border border-gray-200 overflow-hidden">
              <table class="w-full text-xs">
                <thead class="bg-gray-100 text-gray-700">
                  <tr>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('delivery_number') }}</th>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('date') }}</th>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('partner') }}</th>
                    <th class="px-2 py-1 text-right font-semibold">{{ $t('quantity') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="(data.deliveries || []).length === 0">
                    <td colspan="4" class="px-2 py-2 text-center text-gray-500">{{ $t('no_results') }}</td>
                  </tr>
                  <tr
                    v-for="d in (data.deliveries || [])"
                    :key="`d-${d.id}`"
                    class="border-t border-gray-100"
                  >
                    <td class="px-2 py-1 font-medium text-gray-900">{{ d.delivery_number || '-' }}</td>
                    <td class="px-2 py-1 text-gray-700">{{ d.delivery_date || '-' }}</td>
                    <td class="px-2 py-1 text-gray-700">{{ d.partner_name || '-' }}</td>
                    <td class="px-2 py-1 text-right tabular-nums">{{ formatQty(d.quantity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h5 class="text-xs font-semibold text-blue-900 mb-1">{{ $t('returns') }}</h5>
            <div class="rounded-md border border-gray-200 overflow-hidden">
              <table class="w-full text-xs">
                <thead class="bg-gray-100 text-gray-700">
                  <tr>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('return_number') }}</th>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('date') }}</th>
                    <th class="px-2 py-1 text-left font-semibold">{{ $t('returned_by') }}</th>
                    <th class="px-2 py-1 text-right font-semibold">{{ $t('quantity') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="(data.returns || []).length === 0">
                    <td colspan="4" class="px-2 py-2 text-center text-gray-500">{{ $t('no_results') }}</td>
                  </tr>
                  <tr
                    v-for="r in (data.returns || [])"
                    :key="`r-${r.id}`"
                    class="border-t border-gray-100"
                  >
                    <td class="px-2 py-1 font-medium text-gray-900">{{ r.return_number || '-' }}</td>
                    <td class="px-2 py-1 text-gray-700">{{ r.return_date || '-' }}</td>
                    <td class="px-2 py-1 text-gray-700">{{ r.returned_by || '-' }}</td>
                    <td class="px-2 py-1 text-right tabular-nums">{{ formatQty(r.quantity) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button type="button" class="button is-light" @click="close">
          {{ $t('close') }}
        </button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'

function emptyData() {
  return {
    received_total: 0,
    delivered_total: 0,
    returned_total: 0,
    current_stock: 0,
    deliveries: [],
    returns: [],
  }
}

export default {
  name: 'warehouse-inventory-traceability-modal',
  props: {
    value: { type: Boolean, default: false },
    projectId: { type: [Number, String], required: true },
    inventoryId: { type: [Number, String], default: null },
    /** Datos opcionales para mostrar en la cabecera del modal (no se piden al backend). */
    headerInfo: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      isLoading: false,
      data: emptyData(),
    }
  },
  computed: {
    normalizedProjectId() {
      const n = Number(this.projectId)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    normalizedInventoryId() {
      const n = Number(this.inventoryId)
      return Number.isFinite(n) && n > 0 ? n : null
    },
    header() {
      const info = this.headerInfo || {}
      return {
        unique_material_id: info.unique_material_id || '',
        description: info.description || '',
        packing_list_number: this.data.packing_list_number || info.packing_list_number || '',
      }
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.load()
      } else {
        this.data = emptyData()
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    formatQty(value) {
      if (value == null || Number.isNaN(Number(value))) return '-'
      return Number(value).toLocaleString('es-ES', { maximumFractionDigits: 2 })
    },
    async load() {
      if (!this.normalizedProjectId || !this.normalizedInventoryId) return

      this.isLoading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.normalizedProjectId}/warehouse/inventory/${this.normalizedInventoryId}/traceability`,
        )
        this.data = {
          packing_list_number: data?.packing_list_number || '',
          received_total: Number(data?.received_total || 0),
          delivered_total: Number(data?.delivered_total || 0),
          returned_total: Number(data?.returned_total || 0),
          current_stock: Number(data?.current_stock || 0),
          deliveries: Array.isArray(data?.deliveries) ? data.deliveries : [],
          returns: Array.isArray(data?.returns) ? data.returns : [],
        }
      } catch (e) {
        this.data = emptyData()
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
